import { BadgeType } from 'src/interface/common';

import { INVOICE_COLUMN_CLASSNAME_MAP, INVOICE_COLUMN_TRANSLATION_MAP, POD_COLUMN_CLASSNAME_MAP, POD_COLUMN_TRANSLATION_MAP } from './documentHub.const';

export const formatFileSize = (sizeInKB: number) => {
    if (sizeInKB >= 1024) {
        return `${(sizeInKB / 1024).toFixed(0)} Mb`;
    }

    return `${sizeInKB} KB`;
};

// Number of days before the due date at which an invoice starts reading as "due soon" (amber).
// Below this it is urgent-but-not-late; at or beyond it, it is simply informational (neutral).
const DUE_SOON_THRESHOLD_DAYS = 7;

/**
 * Payment urgency buckets for the Days Pending column. Each maps 1:1 to a badge colour and an icon
 * in DaysPendingBadge — add a state here and the badge must gain a matching case.
 *   settled  — nothing outstanding and not paid either (no due date on record)
 *   paid     — invoice has actually been paid (client decision — gets its own green checkmark +
 *              "Paid" label, instead of collapsing into the same bare dash as `settled`)
 *   overdue  — the due date has passed
 *   dueSoon  — due today, tomorrow, or within DUE_SOON_THRESHOLD_DAYS
 *   onTime   — comfortably ahead of the due date
 */
export type DaysPendingState = 'settled' | 'paid' | 'overdue' | 'dueSoon' | 'onTime';

export interface IDaysPendingDisplay {
    state: DaysPendingState;
    label: string;
    /** Historical name from when this rendered as a BadgeCmp pill — now just the icon-colour key. Undefined for `settled`, which renders a bare em dash with no icon. */
    badgeType?: BadgeType;
}

/**
 * getDaysPendingDisplay — urgency state + label for the Days Pending column.
 *
 * `daysPending` comes from the API as `dueDate - today` in whole days, so the SIGN carries the
 * meaning and is easy to read backwards:
 *    > 0  => that many days REMAIN before the invoice is due
 *    = 0  => due today
 *    < 0  => that many days OVERDUE
 *   null  => nothing outstanding (paid, or no due date recorded)
 *
 * The original rendering showed the raw signed integer ("+57" / "-31") and coloured it INVERTED —
 * positive (days still remaining) was red and negative (genuinely overdue) was green. Returning an
 * explicit direction word plus a semantic state means the sign can no longer be misread.
 *
 * Presentation-free by design (no JSX, no class names) so it stays trivially unit-testable;
 * DaysPendingBadge owns the colour and icon mapping.
 */
export const getDaysPendingDisplay = (daysPending: number | null | undefined, t: (key: string, options?: Record<string, unknown>) => string, status?: string): IDaysPendingDisplay => {
    if (daysPending === null || daysPending === undefined) {
        // Client decision — a paid invoice shows "Paid" + a green checkmark, not a bare dash.
        if (status?.toLowerCase() === 'paid') {
            return { state: 'paid', label: t('Common.Paid'), badgeType: 'success' };
        }

        return { state: 'settled', label: '-' };
    }

    if (daysPending < 0) {
        return {
            state: 'overdue',
            // Labels are abbreviated ("31d overdue") to keep the pill compact in a dense table.
            // The "d" form needs no singular variant — "1d overdue" reads correctly as-is.
            label: t('DocumentHub.Days_Overdue', { count: Math.abs(daysPending) }),
            // 'red' (danger50/danger500), not 'error' — BadgeCmp maps 'error' to ORANGE.
            badgeType: 'red',
        };
    }

    if (daysPending === 0) {
        return { state: 'dueSoon', label: t('DocumentHub.Due_Today'), badgeType: 'warning' };
    }

    if (daysPending === 1) {
        return { state: 'dueSoon', label: t('DocumentHub.Due_Tomorrow'), badgeType: 'warning' };
    }

    // Approaching the due date is worth flagging amber; anything further out reads as healthy.
    if (daysPending <= DUE_SOON_THRESHOLD_DAYS) {
        return { state: 'dueSoon', label: t('DocumentHub.Days_Left', { count: daysPending }), badgeType: 'warning' };
    }

    return { state: 'onTime', label: t('DocumentHub.Days_Left', { count: daysPending }), badgeType: 'success' };
};

// `includeAction` — whether the table ends with the icon-only action column. The invoice table
// dropped its per-row Download icon (client request 2026-09-16), so it passes false; a saved
// column setting that still carries an 'action' entry is filtered out rather than rendered.
const buildHeadCells = (columns: any[], hasData: boolean, t: (key: string) => string, classnameMap: Record<string, string>, translationMap: Record<string, string>, includeAction: boolean = true) => {
    const sorted = [...columns].filter((col) => includeAction || col.id !== 'action').sort((a, b) => a.displayOrder - b.displayOrder);
    const actionIndex = sorted.findIndex((col) => col.id === 'action');

    if (actionIndex !== -1) {
        const action = sorted.splice(actionIndex, 1)[0];
        sorted.push(action);
    } else if (includeAction) {
        sorted.push({ id: 'action', name: '', visible: true, displayOrder: sorted.length + 1 });
    }

    return sorted.map((col) => {
        const isAction = col.id === 'action';
        const headCell: any = {
            ...col,
            // 'pod' is an icon-only column with no backend sort mapping — never sortable.
            // ('podUploadedAt' IS sortable — BE maps it to the SQL latestPodAt on both row kinds.)
            sortable: isAction || col.id === 'pod' ? false : hasData,
            rowClassName: classnameMap[col.id] || '',
        };

        if (!isAction) {
            const key = translationMap[col.id];
            headCell.name = key ? t(key) : col.name;
        }

        return headCell;
    });
};

export const transformInvoiceColumnsToHeadCells = (columns: any[], hasData: boolean, t: (key: string) => string) =>
    buildHeadCells(columns, hasData, t, INVOICE_COLUMN_CLASSNAME_MAP, INVOICE_COLUMN_TRANSLATION_MAP, false);

export const transformPodColumnsToHeadCells = (columns: any[], hasData: boolean, t: (key: string) => string) =>
    buildHeadCells(columns, hasData, t, POD_COLUMN_CLASSNAME_MAP, POD_COLUMN_TRANSLATION_MAP);

// "POD Upload Date" for an invoice row = the newest upload across its visible PODs. Prefers the
// BE-computed `podUploadedAt`; falls back to reducing `podDocuments[].dateUploaded` client-side so
// the column still renders correctly if the FE is deployed ahead of the BE (or for cached rows).
// Returns null when the row has no POD (or none carries a parseable date).
export const getLatestPodUploadedAt = (row: any): string | null => {
    if (row?.podUploadedAt) return row.podUploadedAt;

    const docs: any[] = Array.isArray(row?.podDocuments) ? row.podDocuments : [];
    let latest: string | null = null;
    let latestMs = -Infinity;

    docs.forEach((doc) => {
        const ms = doc?.dateUploaded ? new Date(doc.dateUploaded).getTime() : NaN;

        if (!Number.isNaN(ms) && ms > latestMs) {
            latestMs = ms;
            latest = doc.dateUploaded;
        }
    });

    return latest;
};

export const formatInvoiceRowData = (rowData: any[]) => {
    if (!rowData.length) return [];

    return rowData.slice(0, 1).map((inv) => [
        { id: 'invoiceNumber', name: 'Invoice', data: { invoiceNumber: inv?.invoiceNumber } },
        { id: 'orderId', name: 'Order ID', data: { orderId: inv?.orderId } },
        { id: 'status', name: 'Status', data: { status: inv?.status } },
        { id: 'dueDate', name: 'Due Date', data: { dueDate: inv?.dueDate } },
        { id: 'dateInvoiced', name: 'Date Invoiced', data: { dateInvoiced: inv?.dateInvoiced } },
        { id: 'lastUpdated', name: 'Last Updated', data: { lastUpdated: inv?.lastUpdated } },
        { id: 'totalPrice', name: 'Total Price', data: { totalPrice: inv?.totalPrice } },
        { id: 'paymentTerms', name: 'Payment Terms', data: { paymentTerms: inv?.paymentTerms } },
        { id: 'daysPending', name: 'Days Pending', data: { daysPending: inv?.daysPending } },
        { id: 'pod', name: 'POD', data: { podDocuments: inv?.podDocuments } },
        { id: 'podUploadedAt', name: 'POD Upload Date', data: { podUploadedAt: getLatestPodUploadedAt(inv) } },
    ]);
};

export const formatPodRowData = (rowData: any[]) => {
    if (!rowData.length) return [];

    return rowData.slice(0, 1).map((pod) => [
        { id: 'fileName', name: 'File Name', data: { fileName: pod?.fileName } },
        { id: 'orderId', name: 'Order ID', data: { orderId: pod?.orderId } },
        { id: 'origin', name: 'Origin', data: { originAddress: pod?.originAddress } },
        { id: 'destination', name: 'Destination', data: { destinationAddress: pod?.destinationAddress } },
        { id: 'deliveryDate', name: 'Delivery Date', data: { deliveryDate: pod?.deliveryDate } },
        { id: 'carrier', name: 'Carrier', data: { carrierData: pod?.carrierData } },
        { id: 'dateUploaded', name: 'Last Updated', data: { dateUploaded: pod?.dateUploaded } },
    ]);
};
